/*
 * flashmeeting-fe
 *
 * Author: Simon MAQUAIRE
 * Copyright (c) 2026 Prodeo - All Rights Reserved
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CommonGlobalSettingsService, CrossIcon, FileExcelIcon, RootState, UploadIcon, WarningIcon } from '@umanforge/common';
import { useDispatch, useSelector } from 'react-redux';
import BaseModal from '@common_components/modals/baseModal.component';
import { ChartOfSectionApi } from 'interfaces/chartOfSection.interface';
import { ChartValueService } from 'services/chartValue.service';
import { IMPORT_MODE } from 'constants/chart.constants';
import SendFileInput from '@common_components/inputs/sendFileInput.component';
import { Loading } from '@common_components/loading.component';

type ImportChartValueModalProps = {
  isOpen: boolean;
  onClose: () => void;
  chartInfo: ChartOfSectionApi;
  onSaved?: () => void;
};

const ImportChartValueModal: React.FC<ImportChartValueModalProps> = ( {
  isOpen,
  onClose,
  chartInfo,
  onSaved
} ) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );
  const [ selectedFile, setSelectedFile ] = useState<File | null>( null );
  const [ datesAlredyUsed, setDatesAlredyUsed ] = useState<string[]>( [] );
  const [ maxFileSize, setMaxFileSize ] = useState<number>( 0 );

  useEffect( () => {
    if ( isOpen ) {
      CommonGlobalSettingsService.getMaxFileSize( token, dispatch )
        .then( setMaxFileSize );
    }
  }, [ isOpen ] );

  useEffect( () => {
    if ( isOpen ) {
      setDatesAlredyUsed( [] );
    }
  }, [ selectedFile ] );

  const importValueOfFile = useCallback( ( file: File, mode?: typeof IMPORT_MODE[keyof typeof IMPORT_MODE] ) => {
    setIsLoading( true );
    ChartValueService.importChartValue( chartInfo.id, file, mode, token, dispatch, undefined, undefined )
      .then( response => {
        if ( response.isValid && onSaved ) {
          onSaved();
          onClose();
        } else {
          setDatesAlredyUsed( response.dates ?? [] );
        }
      } )
      .finally( () => {
        setIsLoading( false );
      } );
  }, [] );


  return (
    <BaseModal
      isOpen={ isOpen }
      onClose={ onClose }
      noConfirmationModal={ true }
      title={ t( 'import_values_on_chart', { chartTitle: chartInfo.title } ) /* Importation des valeurs sur ${ chartInfo.title }  */ }
    >
      <div className='relative'>
        {
          isLoading && (
            <div className='absolute flex h-full w-full items-center justify-center bg-[#808080AA] z-10'>
              <Loading size={ 100 }/>
            </div>
          )
        }
        <div className="flex flex-col justify-center items-center gap-10 px-6 py-4">
          {
            selectedFile == null ?
              <SendFileInput
                label=''
                maxFileSize={ maxFileSize }
                clearAfterChange
                onChange={ files => {
                  let file = null;
                  if ( files.length > 0 ) {
                    file = files[ 0 ];
                  }
                  setSelectedFile( file );
                } }
                acceptedFormats={ [ 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ] }
              />
              :
              <div
                className={`
                  relative
                  flex flex-col w-full h-full gap-2 p-0.5
                  items-center justify-center text-center
                  max-w-[160px] max-h-[200px] min-w-[95px] min-h-[160px]
                  rounded-md border border-gray-300 shadow shadow-gray-400
                  transition ease-in-out delay-50
                `}
              >
                <div className='flex flex-col w-full h-full justify-center select-none'>
                  <FileExcelIcon className='w-full h-full pl-2 pr-6 py-2'/>
                  <span className='underline italic truncate px-1'>
                    { selectedFile.name }
                  </span>
                </div>

                <div className='absolute top-0 right-0 m-0.5 rounded-full overflow-hidden'>
                  <Button
                    className='bg-transparent hover:bg-transparent text-black hover:text-black trans-fast hover:rotate-90'
                    icon={ <CrossIcon className='p-0.5'/> }
                    onClick={ () => { setSelectedFile( null ); } }
                  />
                </div>
              </div>
          }
          {
            datesAlredyUsed.length > 0 ?
              <div className='flex flex-col w-full gap-4'>
                <span className='flex text-red-800 items-center bg-red-200 p-0.5 rounded-lg'>
                  <WarningIcon className='w-10 h-10'/>
                  { t( 'translation:some_date_have_already_value' ) }
                </span>
                <div className='flex gap-4 w-full'>
                  <Button
                    label={ t( 'translation:ignore_values_with_date_already_used' ) }
                    icon={ <UploadIcon className='p-1.5'/> }
                    className={ `w-full px-3 py-4 bg-blue-50 border border-blue-200 ${ selectedFile == null ? 'text-gray-500' : 'text-blue-600' } hover:bg-blue-100 transition` }
                    disable={ selectedFile == null }
                    onClick={ () => {
                      if ( selectedFile && !isLoading ) {
                        importValueOfFile( selectedFile, IMPORT_MODE.IGNORE );
                      }
                    } }
                  />
                  <Button
                    label={ t( 'translation:override_values_with_date_already_used' ) }
                    icon={ <UploadIcon className='p-1.5'/> }
                    className={ `w-full px-3 py-4 bg-blue-50 border border-blue-200 ${ selectedFile == null ? 'text-gray-500' : 'text-blue-600' } hover:bg-blue-100 transition` }
                    disable={ selectedFile == null }
                    onClick={ () => {
                      if ( selectedFile && !isLoading ) {
                        importValueOfFile( selectedFile, IMPORT_MODE.OVERRIDE );
                      }
                    } }
                  />
                </div>
              </div>
              :
              <Button
                label={ t( 'common:import' ) }
                icon={ <UploadIcon className='p-1.5'/> }
                className={ `w-full px-3 py-4 bg-blue-50 border border-blue-200 ${ selectedFile == null ? 'text-gray-500' : 'text-blue-600' } hover:bg-blue-100 transition` }
                disable={ selectedFile == null }
                onClick={ () => {
                  if ( selectedFile && !isLoading ) {
                    importValueOfFile( selectedFile, IMPORT_MODE.UNDEFINED );
                  }
                } }
              />
          }
        </div>
      </div>
    </BaseModal>
  );
};

export default ImportChartValueModal;
